import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  DataTable,
  Dialog,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Select,
  useToast,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { api, isApiError } from '@/lib/api';
import styles from './parity.module.css';

type Structure = 'separate' | 'one';
export interface LegalEntity {
  key: string;
  legalName: string | null;
  ssmNo: string | null;
  tin: string | null;
  sstRegistered: boolean | null;
  sstNo: string | null;
  invoicePrefix: string | null;
  bankName: string | null;
  bankAccount: string | null;
  branchCodes: string[];
  missing: string[];
  configured: boolean;
}
export interface LegalSetup {
  structure: Structure | null;
  entities: LegalEntity[];
  branches: {
    branch: { id: string; code: string; name: string };
    entityKey: string | null;
  }[];
  configured: boolean;
  updatedAt: string | null;
}

const STRUCTURE: Record<Structure, string> = {
  separate: 'Each clinic is its own company',
  one: 'One company, all branches',
};
const pending = <Badge tone="warning">Pending</Badge>;
const show = (v: string | null) => v ?? pending;

/**
 * Books › Companies (RL-1032): the owners' legal entity per branch. Values
 * come only from the saved setup; anything not entered shows as Pending.
 */
export function LegalEntities() {
  const { hasPermission } = useSession();
  const canEdit = hasPermission('books:manage');
  const [editing, setEditing] = useState(false);
  const q = useQuery({
    queryKey: ['books', 'legal-entities'],
    queryFn: ({ signal }) =>
      api
        .get<LegalSetup>('/finance/legal-entities', { signal })
        .then((r) => r.data),
  });
  if (q.isLoading) return <LoadingState />;
  if (q.error || !q.data)
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const d = q.data;
  const entityOf = (key: string | null) =>
    d.entities.find((e) => e.key === key) ?? null;
  const year = new Date().getFullYear();
  return (
    <>
      <Card
        title="Legal entities"
        flush
        actions={
          <div className={styles.rowActions}>
            {d.configured ? (
              <Badge tone="success">Configured</Badge>
            ) : (
              <Badge tone="warning">Not configured</Badge>
            )}
            {canEdit && (
              <Button size="sm" onClick={() => setEditing(true)}>
                {d.structure ? 'Edit' : 'Set up'}
              </Button>
            )}
          </div>
        }
      >
        <div style={{ padding: '14px 18px' }}>
          <dl className={styles.kv}>
            <dt>Structure</dt>
            <dd>{d.structure ? STRUCTURE[d.structure] : pending}</dd>
          </dl>
          <p className={styles.sub}>
            A decision for the clinic owners. It sets invoice numbering, tax
            registration, e-Invoice identity and the bank account used. Values
            are entered by Finance from the owners&apos; documents; nothing is
            pre-filled.
          </p>
        </div>
        <DataTable
          caption="Legal entities by branch"
          rows={d.branches}
          rowKey={(b) => b.branch.id}
          columns={[
            {
              key: 'b',
              header: 'Branch',
              render: (b) => <strong>{b.branch.name}</strong>,
            },
            {
              key: 'c',
              header: 'Company',
              render: (b) => {
                const e = entityOf(b.entityKey);
                return e ? (
                  <>
                    {show(e.legalName)}
                    <span className={styles.sub}>
                      SSM {e.ssmNo ?? 'pending'}
                    </span>
                  </>
                ) : (
                  <Badge tone="warning">No company yet</Badge>
                );
              },
            },
            {
              key: 't',
              header: 'Tax (TIN)',
              render: (b) =>
                entityOf(b.entityKey) ? (
                  <span className={styles.mono}>
                    {show(entityOf(b.entityKey)!.tin)}
                  </span>
                ) : (
                  '—'
                ),
            },
            {
              key: 's',
              header: 'SST',
              render: (b) => {
                const e = entityOf(b.entityKey);
                if (!e) return '—';
                if (e.sstRegistered === null) return pending;
                return e.sstRegistered ? show(e.sstNo) : 'Not registered';
              },
            },
            {
              key: 'i',
              header: 'Invoice numbers',
              render: (b) => {
                const e = entityOf(b.entityKey);
                return e?.invoicePrefix ? (
                  <span
                    className={styles.mono}
                  >{`INV-${e.invoicePrefix}-${year}-…`}</span>
                ) : e ? (
                  pending
                ) : (
                  '—'
                );
              },
            },
            {
              key: 'k',
              header: 'Bank',
              render: (b) => {
                const e = entityOf(b.entityKey);
                if (!e) return '—';
                return e.bankName && e.bankAccount
                  ? `${e.bankName} ••••${e.bankAccount.slice(-4)}`
                  : pending;
              },
            },
          ]}
        />
        <p className={styles.cardFoot}>
          Group reports add the companies together; each company&apos;s books,
          tax and bank stay separate. Panel invoice numbers keep their current
          panel-code format until per-company numbering is switched on.
        </p>
      </Card>
      {editing && <EditDialog d={d} onClose={() => setEditing(false)} />}
    </>
  );
}

type Draft = Omit<LegalEntity, 'missing' | 'configured'>;
const blank = (key: string, branchCodes: string[]): Draft => ({
  key,
  legalName: null,
  ssmNo: null,
  tin: null,
  sstRegistered: null,
  sstNo: null,
  invoicePrefix: null,
  bankName: null,
  bankAccount: null,
  branchCodes,
});

function EditDialog({ d, onClose }: { d: LegalSetup; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const codes = d.branches.map((b) => b.branch.code);
  const [structure, setStructure] = useState<Structure>(
    d.structure ?? 'separate',
  );
  const [entities, setEntities] = useState<Draft[]>(() =>
    d.entities.map(({ missing: _m, configured: _c, ...e }) => e),
  );
  const shaped: Draft[] =
    structure === 'one'
      ? [{ ...(entities[0] ?? blank('e1', codes)), branchCodes: codes }]
      : codes.map((code, i) => ({
          ...(entities.find(
            (e) => e.branchCodes.length === 1 && e.branchCodes[0] === code,
          ) ??
            entities[i] ??
            blank(`e${i + 1}`, [code])),
          key: `e${i + 1}`,
          branchCodes: [code],
        }));
  const save = useMutation({
    mutationFn: () =>
      api.put('/finance/legal-entities', { structure, entities: shaped }),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['books', 'legal-entities'] }),
  });
  const set = (i: number, patch: Partial<Draft>) =>
    setEntities(shaped.map((e, j) => (j === i ? { ...e, ...patch } : e)));
  const text = (i: number, k: keyof Draft, label: string, placeholder = '') => (
    <Field label={label}>
      <Input
        value={(shaped[i][k] as string | null) ?? ''}
        placeholder={placeholder}
        onChange={(e) => set(i, { [k]: e.target.value || null })}
      />
    </Field>
  );
  return (
    <Dialog
      open
      wide
      title="Legal entities"
      onClose={onClose}
      dismissible={!save.isPending}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={() =>
              void save
                .mutateAsync()
                .then(() => {
                  toast.show({ tone: 'success', title: 'Saved' });
                  onClose();
                })
                .catch((err: unknown) =>
                  toast.show({
                    tone: 'error',
                    title: 'Not saved',
                    body: isApiError(err) ? err.message : undefined,
                    error: err,
                  }),
                )
            }
          >
            Save
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <p className={styles.notice}>
          Enter only the owners&apos; confirmed details. Leave a field empty to
          keep it pending.
        </p>
        <Field label="Structure">
          <Select
            value={structure}
            onChange={(e) => setStructure(e.target.value as Structure)}
            options={(Object.keys(STRUCTURE) as Structure[]).map((s) => ({
              value: s,
              label: STRUCTURE[s],
            }))}
          />
        </Field>
        {shaped.map((e, i) => (
          <Card
            key={e.key}
            title={
              structure === 'one'
                ? 'Company (all branches)'
                : `Company · ${d.branches.find((b) => b.branch.code === e.branchCodes[0])?.branch.name ?? e.branchCodes[0]}`
            }
          >
            <div className={styles.form}>
              {text(i, 'legalName', 'Legal name')}
              {text(i, 'ssmNo', 'SSM registration', '12 digits')}
              {text(i, 'tin', 'Tax number (TIN)', 'e.g. C followed by digits')}
              <Field label="SST registered">
                <Select
                  value={
                    e.sstRegistered === null
                      ? ''
                      : e.sstRegistered
                        ? 'yes'
                        : 'no'
                  }
                  placeholder="Pending"
                  onChange={(ev) =>
                    set(i, {
                      sstRegistered:
                        ev.target.value === ''
                          ? null
                          : ev.target.value === 'yes',
                      ...(ev.target.value !== 'yes' ? { sstNo: null } : {}),
                    })
                  }
                  options={[
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                  ]}
                />
              </Field>
              {e.sstRegistered &&
                text(i, 'sstNo', 'SST number', 'W10-1808-31000001')}
              {text(
                i,
                'invoicePrefix',
                'Invoice prefix',
                '2 to 8 letters or digits',
              )}
              {text(i, 'bankName', 'Bank')}
              {text(i, 'bankAccount', 'Bank account number', 'digits only')}
            </div>
          </Card>
        ))}
      </div>
    </Dialog>
  );
}
